// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Calendar, MapPin, Users, Trophy, BarChart3, Clock, Share2, Bell } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatTime } from '@/lib/dates';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import BracketVisualizer from '@/components/tournaments/BracketVisualizer';
import NotificationSignupModal from '@/components/tournaments/NotificationSignupModal';
import { parseLocalDate } from '@/utils/formatters';

interface Tournament {
  id: string;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  tournament_type: string;
  status: string;
  registration_deadline?: string;
  max_teams: number;
  entry_fee_cents?: number;
  rules?: string;
  sport_id?: string;
  venue_id?: string;
  age_group?: string;
  sports?: {
    name: string;
  };
  venues?: {
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  };
}

interface TournamentStats {
  total_matches: number;
  completed_matches: number;
  scheduled_matches: number;
  registered_teams: number;
}

const TournamentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [brackets, setBrackets] = useState<any[]>([]);
  const [stats, setStats] = useState<TournamentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTournament();
      fetchStats();
      fetchMatches();
      fetchBrackets();
    }
  }, [id]);

  const fetchTournament = async () => {
    console.log("🔍 Fetching tournament details for ID:", id);
    
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      console.log("📊 Tournament detail data:", data);
      console.log("❌ Tournament detail error:", error);

      if (error) throw error;
      
      if (!data) {
        throw new Error('Tournament not found');
      }
      
      setTournament(data);
    } catch (error) {
      console.error('Error fetching tournament:', error);
      toast({
        title: "Error",
        description: "Failed to load tournament details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data: matches, error } = await supabase
        .from('tournament_matches')
        .select('status')
        .eq('tournament_id', id);

      if (error) throw error;

      const totalMatches = matches?.length || 0;
      const completedMatches = matches?.filter(m => m.status === 'completed').length || 0;
      const scheduledMatches = matches?.filter(m => m.status === 'scheduled').length || 0;

      setStats({
        total_matches: totalMatches,
        completed_matches: completedMatches,
        scheduled_matches: scheduledMatches,
        registered_teams: 0, // Would need team registration data
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const { data, error } = await supabase
        .from('tournament_matches')
        .select('*')
        .eq('tournament_id', id)
        .order('round_number', { ascending: true })
        .order('match_number', { ascending: true });

      if (error) throw error;
      setMatches(data || []);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const fetchBrackets = async () => {
    try {
      const { data, error } = await supabase
        .from('tournament_brackets')
        .select('*')
        .eq('tournament_id', id)
        .order('seed_number', { ascending: true });

      if (error) throw error;
      setBrackets(data || []);
    } catch (error) {
      console.error('Error fetching brackets:', error);
    }
  };

  const shareTournament = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: tournament?.name,
          text: `Check out this tournament: ${tournament?.name}`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard
        await navigator.clipboard.writeText(url);
        toast({
          title: "Link copied!",
          description: "Tournament link copied to clipboard",
        });
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied!",
        description: "Tournament link copied to clipboard",
      });
    }
  };

  const getTournamentStatus = (tournament: Tournament) => {
    // If no dates available, use the tournament status
    if (!tournament.start_date || !tournament.end_date) {
      if (tournament.status === 'active') return { label: 'Live', variant: 'destructive' };
      if (tournament.status === 'setup') return { label: 'Upcoming', variant: 'secondary' };
      return { label: tournament.status, variant: 'outline' };
    }
    
    const now = new Date();
    const startDate = parseLocalDate(tournament.start_date);
    const endDate = parseLocalDate(tournament.end_date);
    
    if (startDate > now) return { label: 'Upcoming', variant: 'secondary' };
    if (startDate <= now && endDate >= now) return { label: 'Live', variant: 'destructive' };
    return { label: 'Completed', variant: 'outline' };
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Loading tournament...</div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center py-12">
          <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Tournament not found</h3>
          <p className="text-muted-foreground mb-4">
            The tournament you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild>
            <Link to="/tournaments">Browse Tournaments</Link>
          </Button>
        </div>
      </div>
    );
  }

  const status = getTournamentStatus(tournament);

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Badge variant={status.variant as any} className="text-sm">
                {status.label}
              </Badge>
              {tournament.sports && (
                <Badge variant="outline">{tournament.sports.name}</Badge>
              )}
            </div>
          <h1 className="text-3xl font-bold">{tournament.name}</h1>
          {tournament.description && (
            <p className="text-lg text-muted-foreground">{tournament.description}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={() => setShowNotificationModal(true)} variant="outline">
            <Bell className="h-4 w-4 mr-2" />
            Get Notified
          </Button>
          <Button onClick={shareTournament} variant="outline">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Trophy className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_matches}</p>
                  <p className="text-sm text-muted-foreground">Total Matches</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.completed_matches}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.scheduled_matches}</p>
                  <p className="text-sm text-muted-foreground">Scheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tournament Details */}
      <Tabs defaultValue="info" className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-12 bg-muted p-1 rounded-lg">
          <TabsTrigger 
            value="info" 
            className="text-sm font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
          >
            Information
          </TabsTrigger>
          <TabsTrigger 
            value="schedule" 
            className="text-sm font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
          >
            Schedule
          </TabsTrigger>
          <TabsTrigger 
            value="bracket" 
            className="text-sm font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
          >
            Bracket
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Tournament Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {tournament.start_date && tournament.end_date && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Tournament Dates</p>
                        <p className="text-sm text-muted-foreground">
                          {parseLocalDate(tournament.start_date).toLocaleDateString()} - {' '}
                          {parseLocalDate(tournament.end_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Trophy className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Format</p>
                      <p className="text-sm text-muted-foreground capitalize">
                        {tournament.tournament_type.replace('_', ' ')}
                      </p>
                    </div>
                  </div>

                  {tournament.age_group && (
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Age Group</p>
                        <Badge variant="secondary" className="text-sm">
                          {tournament.age_group}
                        </Badge>
                      </div>
                    </div>
                  )}


                  {tournament.entry_fee_cents && tournament.entry_fee_cents > 0 && (
                    <div className="flex items-center gap-3">
                      <Trophy className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Entry Fee</p>
                        <p className="text-sm text-muted-foreground">
                          ${(tournament.entry_fee_cents / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {tournament.venues && (
              <Card>
                <CardHeader>
                  <CardTitle>Venue Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">{tournament.venues.name}</p>
                      <div className="text-sm text-muted-foreground space-y-1">
                        {tournament.venues.address && (
                          <p>{tournament.venues.address}</p>
                        )}
                        <p>
                          {tournament.venues.city}, {tournament.venues.state} {tournament.venues.zip_code}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {tournament.rules && (
            <Card>
              <CardHeader>
                <CardTitle>Tournament Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap">{tournament.rules}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="schedule">
          <Card>
            <CardHeader>
              <CardTitle>Tournament Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              {matches.length > 0 ? (
                <div className="space-y-4">
                  {matches.map((match) => (
                    <div key={match.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Match {match.match_number}</span>
                            <Badge variant="outline">Round {match.round_number}</Badge>
                            <Badge variant={match.status === 'completed' ? 'default' : 'secondary'}>
                              {match.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {match.team1_name || 'TBD'} vs {match.team2_name || 'TBD'}
                          </div>
                          {match.scheduled_time && (
                            <div className="text-sm text-muted-foreground">
                              <Clock className="h-4 w-4 inline mr-1" />
                              {`${new Date(match.scheduled_time).toLocaleDateString()}, ${formatTime(match.scheduled_time)}`}
                            </div>
                          )}
                        </div>
                        {match.status === 'completed' && (match.team1_score !== null || match.team2_score !== null) && (
                          <div className="text-right">
                            <div className="font-medium">
                              {match.team1_score || 0} - {match.team2_score || 0}
                            </div>
                            {match.winner_id && (
                              <div className="text-sm text-muted-foreground">
                                Winner: {match.team1_id === match.winner_id ? match.team1_name : match.team2_name}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Schedule Coming Soon</h3>
                  <p className="text-muted-foreground mb-4">
                    The tournament schedule will be available once matches are scheduled.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bracket">
          <Card>
            <CardHeader>
              <CardTitle>Tournament Bracket</CardTitle>
            </CardHeader>
            <CardContent>
              <BracketVisualizer 
                matches={matches}
                tournamentType={tournament.tournament_type}
                bracketType="all"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Notification Signup Modal */}
      <NotificationSignupModal
        open={showNotificationModal}
        onOpenChange={setShowNotificationModal}
        tournamentId={tournament.id}
        tournamentName={tournament.name}
      />
    </div>
  );
};

export default TournamentDetailPage;